// Lightweight localStorage-backed data store for GVRewards (frontend-only demo).
// Falls back to mock data when nothing is saved yet.
import {
  brand,
  topThree as mockTopThree,
  leaderboard as mockLeaderboard,
  previousMonths as mockPreviousMonths,
  exclusiveSettings,
  exclusiveTopThree,
  exclusiveLeaderboard,
} from "./mock";

import { siteRequestHeaders } from './lib/siteRequestHeaders';
import { maskApiLeaderboard } from './lib/leaderboardPrivacy';

const KEY = "gv_data_v1";

const defaultState = () => ({
  settings: {
    prizePool: brand.prizePool,
    affiliateCode: brand.affiliateCode,
  },
  topThree: mockTopThree,
  leaderboard: mockLeaderboard,
  previousMonths: mockPreviousMonths,
  exclusive: {
    settings: { ...exclusiveSettings },
    topThree: exclusiveTopThree,
    leaderboard: exclusiveLeaderboard,
  },
});

export const loadState = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw);
    return { ...defaultState(), ...parsed };
  } catch {
    return defaultState();
  }
};

export const saveState = (state) => {
  localStorage.setItem(KEY, JSON.stringify(state));
  window.dispatchEvent(new Event("gv_data_updated"));
};

export const resetState = () => {
  localStorage.removeItem(KEY);
  window.dispatchEvent(new Event("gv_data_updated"));
  return defaultState();
};

// ---- Admin auth (backend JWT) ----
const TOKEN_KEY = "gv_admin_token";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const getAdminToken = () => localStorage.getItem(TOKEN_KEY);
export const setAdminToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};
export const clearAdminToken = () => localStorage.removeItem(TOKEN_KEY);

// Attempt login against backend; returns { ok, error }
export const adminLogin = async (username, password) => {
  try {
    const res = await fetch(`${API}/admin/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: false, error: data.detail || "Invalid credentials" };
    }
    const data = await res.json();
    setAdminToken(data.token);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: "Unable to reach server" };
  }
};

// Verify current token with backend
export const verifyAdmin = async () => {
  const token = getAdminToken();
  if (!token) return false;
  try {
    const res = await fetch(`${API}/admin/verify`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.ok;
  } catch {
    return false;
  }
};

// ---- Public leaderboard (server-configured source) ----
export const fetchLeaderboard = async (board) => {
  try {
    const res = await fetch(`${API}/leaderboard/${board}`, { cache: 'no-store', headers: siteRequestHeaders() });
    if (!res.ok) return { configured: true, entries: [], unavailable: true };
    return maskApiLeaderboard(await res.json());
  } catch {
    return { configured: true, entries: [], unavailable: true };
  }
};

// ---- Public latest videos (YouTube RSS backed) ----
export const fetchVideos = async (signal) => {
  try {
    const res = await fetch(`${API}/videos`, { cache: 'no-store', signal, headers: siteRequestHeaders() });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};
